import { CommonModule } from '@angular/common'
import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AppState } from 'src/app/ngrx/states'
import { search } from 'src/app/ngrx/store/actions'
import { getTheme } from 'src/app/ngrx/store/selectors'
import { PairService } from 'src/app/service/data/pair.service'
import { WordService } from 'src/app/service/data/word.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import {
  PaginatedSearchCommand,
  PaginatedSearchParams,
} from '../../shared/commands/paginated-search.command'
import { DdcDesktopDialogComponent } from '../../shared/ddc/ddc-desktop-dialog/ddc-desktop-dialog.component'
import { DdcGeneralSearchDialogComponent } from '../../shared/ddc/ddc-general-search-dialog/ddc-general-search-dialog.component'
import { DdcMobileSheetComponent } from '../../shared/ddc/ddc-mobile-sheet/ddc-mobile-sheet.component'
import { DdcSearchResultDictionaryComponent } from '../../shared/ddc/ddc-search-result-dictionary/ddc-search-result-dictionary.component'
import { DdcSearchResultFigureComponent } from '../../shared/ddc/ddc-search-result-figure/ddc-search-result-figure.component'
import { DdcSearchResultNoteComponent } from '../../shared/ddc/ddc-search-result-note/ddc-search-result-note.component'
import { DdcSearchResultPairComponent } from '../../shared/ddc/ddc-search-result-pair/ddc-search-result-pair.component'
import { DdcSearchResultQuizComponent } from '../../shared/ddc/ddc-search-result-quiz/ddc-search-result-quiz.component'
import { DdcSearchResultUserComponent } from '../../shared/ddc/ddc-search-result-user/ddc-search-result-user.component'
import { DdcSearchResultWordComponent } from '../../shared/ddc/ddc-search-result-word/ddc-search-result-word.component'
import {
  ArticlesByLanguage,
  DataSearchResult,
  DefaultPagination,
  Languages,
  PartOfSpeech,
  Themes,
} from '../../shared/enums/enums'
import { SelectOption } from '../../shared/interfaces/word'

interface FilterOptions {
  languages: SelectOption[]
  articles: SelectOption[]
  partOfSpeeches: SelectOption[]
  // For pairs - second language filters
  languages2: SelectOption[]
  articles2: SelectOption[]
  partOfSpeeches2: SelectOption[]
}

interface SelectedFilters {
  // For words
  language: string
  article: string
  partOfSpeech: string
  // For pairs
  language1: string
  article1: string
  partOfSpeech1: string
  language2: string
  article2: string
  partOfSpeech2: string
}

@Component({
  selector: 'ddc-search',
  templateUrl: './ddc-search.component.html',
  styleUrls: ['./ddc-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    AngularMaterialsModules,
    InfiniteScrollDirective,
    DdcSearchResultWordComponent,
    DdcSearchResultPairComponent,
    DdcSearchResultUserComponent,
    DdcSearchResultDictionaryComponent,
    DdcSearchResultNoteComponent,
    DdcSearchResultQuizComponent,
    DdcSearchResultFigureComponent,
    DdcGeneralSearchDialogComponent,
  ],
})
export class DdcSearchComponent implements OnInit, OnDestroy {
  _DefaultProfilePic: string =
    'https://st3.depositphotos.com/6672868/13701/v/600/depositphotos_137014128-stock-illustration-user-profile-icon.jpg'

  // Theme properties
  _theme$: Observable<string>
  _isDarkTheme: boolean = false
  _isLightTheme: boolean = true
  _isPaperTheme: boolean = false

  // View modes
  _isFilterMode: boolean = false
  _showGeneralSearch: boolean = true

  // Search properties
  _searchTerm: string = ''
  _selectedTab: 'words' | 'pairs' | 'dictionaries' | 'users' | 'notes' | 'quizzes' | 'figures' =
    'words'
  _results: DataSearchResult[] = []
  _totalResults: number = 0
  _loading: boolean = false
  _loadingMore: boolean = false
  _hasMoreResults: boolean = true

  // Pagination
  _page: number = 1
  _pageSize: number = DefaultPagination.OBJECT_PER_PAGE

  // Filters
  _showFilters: boolean = false
  _selectedFilters: SelectedFilters = {
    // For words
    language: '',
    article: '',
    partOfSpeech: '',
    // For pairs
    language1: '',
    article1: '',
    partOfSpeech1: '',
    language2: '',
    article2: '',
    partOfSpeech2: '',
  }

  _filterOptions: FilterOptions = {
    languages: [],
    articles: [],
    partOfSpeeches: [],
    languages2: [],
    articles2: [],
    partOfSpeeches2: [],
  }

  // Language-dependent article availability
  _selectedLngHasArticle: boolean = false
  _selectedLngHasArticle2: boolean = false

  // Subscriptions
  private searchSubject = new Subject<void>()
  private subscriptions: Subscription[] = []
  _isMobile = false

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this._isMobile = event.target.innerWidth < 500
  }
  constructor(
    private logger: DdcLoggerService,
    private router: Router,
    private route: ActivatedRoute,
    private paginatedSearchCommand: PaginatedSearchCommand,
    public themeService: ThemeService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private word_service: WordService,
    private pair_service: PairService,
  ) {
    this._theme$ = this.store.select(getTheme)
  }

  ngOnInit(): void {
    this.store.dispatch(search())
    this.initializeTheme()
    this.initializeFilterOptions()
    this.setupSearchDebouncing()
    this.processRouteParams()
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  private initializeTheme(): void {
    const themeSub = this._theme$.subscribe(theme => {
      this._isDarkTheme = theme === Themes.DARK
      this._isLightTheme = theme === Themes.LIGHT
      this._isPaperTheme = theme === Themes.PAPER
    })
    this.subscriptions.push(themeSub)
  }

  private processRouteParams(): void {
    const routeSub = this.route.queryParams.subscribe(params => {
      if (params['q']) {
        this._searchTerm = params['q']
        this._isFilterMode = true
        this._showGeneralSearch = false
      }
      if (params['tab']) {
        this._selectedTab = params['tab'] as
          'words' | 'pairs' | 'dictionaries' | 'users' | 'notes' | 'quizzes' | 'figures'
      }
      if (this._searchTerm && this._isFilterMode) {
        this.performSearch()
      }
    })
    this.subscriptions.push(routeSub)
  }

  private initializeFilterOptions(): void {
    // Initialize languages for both word and pair filters
    this._filterOptions.languages = [
      { raw_value: '', view_value: 'Any Language' },
      ...Object.entries(Languages).map(([key, value]) => ({
        raw_value: value[1], // Use the language code
        view_value: value[0], // Use the language name
      })),
    ]

    // Copy languages for second language in pairs
    this._filterOptions.languages2 = [...this._filterOptions.languages]

    // Initialize articles (will be populated based on language selection)
    this._filterOptions.articles = [{ raw_value: '', view_value: 'Any Article' }]
    this._filterOptions.articles2 = [{ raw_value: '', view_value: 'Any Article' }]

    // Initialize parts of speech
    this._filterOptions.partOfSpeeches = [
      { raw_value: '', view_value: 'Any Part of Speech' },
      ...Object.values(PartOfSpeech).map(pos => ({
        raw_value: pos,
        view_value: pos,
      })),
    ]

    // Copy parts of speech for second word in pairs
    this._filterOptions.partOfSpeeches2 = [...this._filterOptions.partOfSpeeches]
  }

  private setupSearchDebouncing(): void {
    const searchSub = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.resetPagination()
        this.performSearch()
      })
    this.subscriptions.push(searchSub)
  }

  onSearchChange(): void {
    if (!this._searchTerm || this._searchTerm.length < 2) {
      this._results = []
      this._totalResults = 0
      return
    }
    this.searchSubject.next()
  }

  onTabChange(
    tab: 'words' | 'pairs' | 'dictionaries' | 'users' | 'notes' | 'quizzes' | 'figures',
  ): void {
    this._selectedTab = tab
    // Reset filters when changing tabs
    this.resetFilters()
    this.resetPagination()
    this.performSearch()
    this.updateUrlParams()
  }

  toggleFilterMode(): void {
    if (this._isFilterMode) {
      // Go back to general search
      this._isFilterMode = false
      this._showGeneralSearch = true
      this.router.navigate(['/search'])
    } else {
      // Enter filter mode
      this._isFilterMode = true
      this._showGeneralSearch = false
      if (this._searchTerm) {
        this.updateUrlParams()
        this.performSearch()
      }
    }
  }

  toggleFilters(): void {
    this._showFilters = !this._showFilters
  }

  resetFilters(): void {
    this._selectedFilters = {
      // For words
      language: '',
      article: '',
      partOfSpeech: '',
      // For pairs
      language1: '',
      article1: '',
      partOfSpeech1: '',
      language2: '',
      article2: '',
      partOfSpeech2: '',
    }

    // Reset article options
    this._selectedLngHasArticle = false
    this._selectedLngHasArticle2 = false
    this._filterOptions.articles = [{ raw_value: '', view_value: 'Any Article' }]
    this._filterOptions.articles2 = [{ raw_value: '', view_value: 'Any Article' }]
  }

  applyFilters(): void {
    this.resetPagination()
    this.performSearch()
  }

  resetPagination(): void {
    this._page = 1
    this._hasMoreResults = true
    this._results = []
  }

  performSearch(): void {
    if (!this._searchTerm || this._searchTerm.length < 2) {
      return
    }

    this._loading = this._page === 1
    this._loadingMore = this._page > 1

    // Map tab names to API types
    const typeMap = {
      words: 'word',
      pairs: 'pair',
      dictionaries: 'dictionary',
      users: 'user',
      notes: 'note',
      quizzes: 'quiz',
      figures: 'figure',
    }

    // Build filter object based on tab
    let filters: any = {}

    if (this._selectedTab === 'words') {
      filters = {
        language: this._selectedFilters.language || undefined,
        article: this._selectedFilters.article || undefined,
        partOfSpeech: this._selectedFilters.partOfSpeech || undefined,
      }
    } else if (this._selectedTab === 'pairs') {
      filters = {
        language1: this._selectedFilters.language1 || undefined,
        article1: this._selectedFilters.article1 || undefined,
        partOfSpeech1: this._selectedFilters.partOfSpeech1 || undefined,
        language2: this._selectedFilters.language2 || undefined,
        article2: this._selectedFilters.article2 || undefined,
        partOfSpeech2: this._selectedFilters.partOfSpeech2 || undefined,
      }
    }

    const searchParams: PaginatedSearchParams = {
      query: this._searchTerm,
      type: typeMap[this._selectedTab] as
        'word' | 'pair' | 'dictionary' | 'user' | 'note' | 'quiz' | 'figure',
      page: this._page,
      pageSize: this._pageSize,
      filters: filters,
    }

    const searchSub = this.paginatedSearchCommand.execute(searchParams).subscribe({
      next: response => {
        this.handlePaginatedSearchResults(response)
      },
      error: error => {
        this.logger.error('Search error:', error)
        this._loading = false
        this._loadingMore = false
      },
    })
    this.subscriptions.push(searchSub)
  }

  handlePaginatedSearchResults(response: any): void {
    this._loading = false
    this._loadingMore = false

    if (this._page === 1) {
      this._results = response.results || []
    } else {
      this._results = [...this._results, ...(response.results || [])]
    }

    this._totalResults = response.totalCount || 0
    this._hasMoreResults = response.hasMore || false
  }

  onScroll(): void {
    if (!this._loadingMore && this._hasMoreResults) {
      this._page++
      this.performSearch()
    }
  }

  updateUrlParams(): void {
    const queryParams: any = {}
    if (this._searchTerm) {
      queryParams.q = this._searchTerm
    }
    if (this._selectedTab !== 'words') {
      queryParams.tab = this._selectedTab
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    })
  }

  openWordDialog(wordId: number): void {
    // Implement word dialog opening logic
    this.logger.info('Opening word dialog for:', wordId)
  }

  openPairDialog(pairId: number): void {
    // Implement pair dialog opening logic
    this.logger.info('Opening pair dialog for:', pairId)
  }

  navigateToDictionary(dictionary: any): void {
    this.router.navigate(['/library/dic', dictionary.id, dictionary.info?.owner])
  }

  navigateToUser(user: any): void {
    this.logger.info('DdcGeneralSearchDialogComponent | navigateToUser() item:', user)

    // Navigate to the user page
    this.router.navigate(['/users/' + user.info.username])
  }

  navigateToNote(note: any): void {
    this.logger.info('DdcSearchComponent | navigateToNote() item:', note)
    this.router.navigate(['/library/note', note.id])
  }

  navigateToQuiz(quiz: any): void {
    this.logger.info('DdcSearchComponent | navigateToQuiz() item:', quiz)
    this.router.navigate(['/library/quiz', quiz.id])
  }

  navigateToFigure(figure: any): void {
    this.logger.info('DdcSearchComponent | navigateToFigure() item:', figure)
    this.router.navigate(['/library/figure', figure.id])
  }

  getActiveFiltersCount(): number {
    let count = 0

    if (this._selectedTab === 'words') {
      if (this._selectedFilters.language) count++
      if (this._selectedFilters.article) count++
      if (this._selectedFilters.partOfSpeech) count++
    } else if (this._selectedTab === 'pairs') {
      if (this._selectedFilters.language1) count++
      if (this._selectedFilters.article1) count++
      if (this._selectedFilters.partOfSpeech1) count++
      if (this._selectedFilters.language2) count++
      if (this._selectedFilters.article2) count++
      if (this._selectedFilters.partOfSpeech2) count++
    }

    return count
  }

  // Language change methods (similar to add-mobile-sheet)
  onLanguageFilterChange(language: string): void {
    this._selectedFilters.language = language
    this.updateArticlesForLanguage(language, 'articles')
    this.applyFilters()
  }

  onLanguage1FilterChange(language: string): void {
    this._selectedFilters.language1 = language
    this.updateArticlesForLanguage(language, 'articles')
    this.applyFilters()
  }

  onLanguage2FilterChange(language: string): void {
    this._selectedFilters.language2 = language
    this.updateArticlesForLanguage(language, 'articles2')
    this.applyFilters()
  }

  onArticleFilterChange(article: string): void {
    if (this._selectedTab === 'words') {
      this._selectedFilters.article = article
    } else {
      this._selectedFilters.article1 = article
    }
    this.applyFilters()
  }

  onArticle2FilterChange(article: string): void {
    this._selectedFilters.article2 = article
    this.applyFilters()
  }

  onPartOfSpeechFilterChange(partOfSpeech: string): void {
    if (this._selectedTab === 'words') {
      this._selectedFilters.partOfSpeech = partOfSpeech
    } else {
      this._selectedFilters.partOfSpeech1 = partOfSpeech
    }
    this.applyFilters()
  }

  onPartOfSpeech2FilterChange(partOfSpeech: string): void {
    this._selectedFilters.partOfSpeech2 = partOfSpeech
    this.applyFilters()
  }

  private updateArticlesForLanguage(
    language: string,
    articlesProperty: 'articles' | 'articles2',
  ): void {
    const articles: SelectOption[] = [{ raw_value: '', view_value: 'Any Article' }]

    if (language && ArticlesByLanguage[language]) {
      const languageArticles = ArticlesByLanguage[language]
      for (const article of languageArticles) {
        articles.push({
          raw_value: article,
          view_value: article,
        })
      }

      if (articlesProperty === 'articles') {
        this._selectedLngHasArticle = true
      } else {
        this._selectedLngHasArticle2 = true
      }
    } else {
      if (articlesProperty === 'articles') {
        this._selectedLngHasArticle = false
        if (this._selectedTab === 'words') {
          this._selectedFilters.article = ''
        } else {
          this._selectedFilters.article1 = ''
        }
      } else {
        this._selectedLngHasArticle2 = false
        this._selectedFilters.article2 = ''
      }
    }

    this._filterOptions[articlesProperty] = articles
  }

  clearAllFilters(): void {
    this.resetFilters()
    this.applyFilters()
  }

  getSingularLabel(tab: string): string {
    const labels = {
      words: 'word',
      pairs: 'pair',
      dictionaries: 'dictionary',
      users: 'user',
      notes: 'note',
      quizzes: 'quiz',
      figures: 'figure',
    }
    return labels[tab as keyof typeof labels] || tab
  }

  getPluralLabel(tab: string): string {
    const labels = {
      words: 'words',
      pairs: 'pairs',
      dictionaries: 'dictionaries',
      users: 'users',
      notes: 'notes',
      quizzes: 'quizzes',
      figures: 'figures',
    }
    return labels[tab as keyof typeof labels] || tab
  }

  /**
   * Show a modern loading overlay
   */
  private showLoadingOverlay(message: string): any {
    // Set loading state to true to show spinner in UI
    this._loading = true

    // Create a simple loading overlay approach
    return {
      close: (): void => {
        // Hide loading overlay
        this._loading = false
      },
    }
  }

  openReadWordDialog(index: number): void {
    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading word details...')

    // Get word by id
    this.word_service.get_word(index).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          const clickedSearchedWord = res.res

          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()

            // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-word', element: clickedSearchedWord }
            const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-word', element: clickedSearchedWord }
            const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
            dialogRef.afterClosed().subscribe(result => {
              if (result === 'word') {
              }
            })
          }
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading word:', error)
        // You could show an error dialog here
      },
    })
  }

  openReadPairDialog(index: number): void {
    // Show loading overlay immediately
    const loadingRef = this.showLoadingOverlay('Loading pair details...')

    // Get pair by id
    this.pair_service.get_pair(index).subscribe({
      next: (res: any) => {
        // Close loading overlay
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }

        if (res) {
          const clickedSearchedPair = res.res
          if (this._isMobile) {
            const dialogConfig = new MatDialogConfig()

            // Get the theme synchronously (if needed, subscribe in advance and store the value in a variable)
            let themeClass = ''
            this._theme$
              .subscribe(theme => {
                themeClass = theme
              })
              .unsubscribe()
            dialogConfig.panelClass = ['mobile-sheet-container', themeClass]

            dialogConfig.position = { bottom: '0' }
            dialogConfig.width = '100%'
            dialogConfig.height = '80vh'
            dialogConfig.maxWidth = '100vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.backdropClass = 'mobile-sheet-backdrop'
            dialogConfig.enterAnimationDuration = '300ms'
            dialogConfig.exitAnimationDuration = '200ms'
            dialogConfig.data = { type: 'read-pair', element: clickedSearchedPair }
            const dialogRef = this.dialog.open(DdcMobileSheetComponent, dialogConfig)
          } else {
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '600px'
            dialogConfig.maxWidth = '90vw'
            dialogConfig.hasBackdrop = true
            dialogConfig.data = { type: 'read-pair', element: clickedSearchedPair }
            const dialogRef = this.dialog.open(DdcDesktopDialogComponent, dialogConfig)
            dialogRef.afterClosed().subscribe(result => {
              if (result === 'pair') {
              }
            })
          }
        }
      },
      error: (error: any) => {
        // Close loading overlay and show error
        if (loadingRef && loadingRef.close) {
          loadingRef.close()
        }
        this.logger.error('Error loading pair:', error)
        // You could show an error dialog here
      },
    })
  }
}
